/**
 * Locale-aware duration formatting via Intl.NumberFormat unit styles.
 *
 * Same reasoning as `format-relative.ts`: Intl already knows every locale's
 * unit abbreviation («2 год» / «2 ч» / "2 hr"), so there is no per-language key
 * to maintain — and no way for one language's suffix to leak into the other
 * three. That leak is why this module exists: a hardcoded `${minutes}м` printed
 * the Russian minute on the Ukrainian, English and Spanish UI alike.
 *
 * `unitDisplay: 'short'` on purpose — 'narrow' collapses Ukrainian minutes to
 * «15х», which reads as nothing at all.
 */
import { resolveLocale } from './utils';

type DurationUnit = 'day' | 'hour' | 'minute' | 'second';

/**
 * A single value with its localized unit: «15 хв», «120,5 хв», "2 hr".
 *
 * Use directly when the caller already holds the value in one unit (hours
 * saved, minutes billed); use `formatDuration` when it holds raw seconds.
 */
export function formatDurationUnit(
  value: number,
  unit: DurationUnit,
  maximumFractionDigits = 0
): string {
  return new Intl.NumberFormat(resolveLocale(), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits,
  }).format(value);
}

/**
 * Duration in at most two units: 45 → «45 с», 200 → «3 хв 20 с»,
 * 8100 → «2 год 15 хв». A zero remainder is dropped («2 год», not «2 год 0 хв»).
 */
export function formatDuration(totalSeconds: number): string {
  const total = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return minutes > 0
      ? `${formatDurationUnit(hours, 'hour')} ${formatDurationUnit(minutes, 'minute')}`
      : formatDurationUnit(hours, 'hour');
  }
  if (minutes > 0) {
    return seconds > 0
      ? `${formatDurationUnit(minutes, 'minute')} ${formatDurationUnit(seconds, 'second')}`
      : formatDurationUnit(minutes, 'minute');
  }
  return formatDurationUnit(seconds, 'second');
}

/**
 * Largest whole unit only: 45 → «45 с», 200 → «3 хв», 8100 → «2 год»,
 * 200000 → «2 дн.». For age/staleness badges where the remainder is noise.
 */
export function formatDurationCoarse(totalSeconds: number): string {
  const total = Math.max(0, Math.round(totalSeconds));
  if (total >= 86_400) return formatDurationUnit(Math.floor(total / 86_400), 'day');
  if (total >= 3_600) return formatDurationUnit(Math.floor(total / 3_600), 'hour');
  if (total >= 60) return formatDurationUnit(Math.floor(total / 60), 'minute');
  return formatDurationUnit(total, 'second');
}
